import { FunctionComponent, useState } from 'react';
import debounce from 'lodash/debounce';
import {
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiContext,
  EuiText,
  EuiFormRow,
} from '@elastic/eui';
import usePersonSearch from '@data/person-search';
import type { PersonBase } from '@domain/person';
import { css } from '@emotion/react';
import { useSession } from 'next-auth/react';

type PersonOption = EuiComboBoxOptionOption<PersonBase>;

const personOption = (person: PersonBase): PersonOption => ({
  label: `${person.firstname} ${person.surname}`,
  value: person,
});

const renderOption = (option: PersonOption) => {
  const value = option.value as PersonBase;
  if (!value.firstname) return;

  return (
    <div>
      <EuiText size="xs">
        {value.firstname} {value.surname}
      </EuiText>
      <EuiText size="xs" color="subdued">
        {value.position}
      </EuiText>
    </div>
  );
};

interface PersonSearchProps {
  value: PersonBase | null;
  onChange: (value: PersonBase | null) => void;
  disabled?: boolean;
}

const PersonSearch: FunctionComponent<PersonSearchProps> = props => {
  const { value: selected, onChange, disabled = false } = props;
  const { data: session } = useSession();

  const [searchValue, setSearchValue] = useState('');

  const isMe = searchValue.trim().toLowerCase() === 'me';
  const effectiveSearchValue = isMe
    ? (session?.user?.idNumber ?? searchValue)
    : searchValue;

  const searchValueWithoutSpaces = effectiveSearchValue.replace(/\s+/g, '');

  const idNumber = /^\d{13}$/.test(searchValueWithoutSpaces)
    ? searchValueWithoutSpaces
    : null;
  const darn = /^\d{8}$/.test(searchValueWithoutSpaces)
    ? parseInt(searchValueWithoutSpaces)
    : null;

  const name = !/^\d+$/.test(effectiveSearchValue)
    ? effectiveSearchValue.trim()
    : '';
  const skip = name.length < 3 && !darn && !idNumber;

  const { loading, people } = usePersonSearch(
    { name: name, idnumber: idNumber ?? undefined, darn: darn ?? undefined },
    skip
  );

  const debouncedHandleSearchChange = debounce((value: string) => {
    setSearchValue(value);
  }, 300);

  const handleSearchChange = (value: string) => {
    debouncedHandleSearchChange(value?.trimEnd());
  };

  const noMatchingOptionsText = () => {
    return (
      <EuiText size="xs">
        <span>
          No results for{' '}
          <span
            css={css`
              font-weight: bold;
            `}>
            {searchValue}
          </span>
          .
        </span>
      </EuiText>
    );
  };

  const i18n = {
    render: () => noMatchingOptionsText,
  };

  return (
    <EuiFormRow
      label="Search for a person"
      helpText="Search by name, DARN or ID number.">
      <EuiContext i18n={i18n}>
        <EuiComboBox
          fullWidth
          async
          isLoading={loading}
          isDisabled={disabled}
          noSuggestions={!isMe && searchValue.trim().length < 3}
          aria-label="Start typing to search for a person"
          placeholder="Start typing to search for a person"
          singleSelection={{ asPlainText: true }}
          selectedOptions={selected == null ? [] : [personOption(selected)]}
          options={(people ?? []).map(personOption)}
          onChange={options =>
            onChange(
              options.length > 0 ? (options[0].value as PersonBase) : null
            )
          }
          rowHeight={50}
          onSearchChange={handleSearchChange}
          renderOption={renderOption}
          isClearable={true}
        />
      </EuiContext>
    </EuiFormRow>
  );
};

export default PersonSearch;
